/**
 * Multi-CDN analytics — how a subdomain's traffic is served across CDN platforms.
 * Two tabs (Traffic / Request) pick the metric; a single-select chip row picks
 * the data source (Total Bytes / Origin Bytes / Hit Rate / Geo Data), each its
 * own Owl endpoint. The chip's response carries both metrics, so switching tabs
 * re-renders from cache — only switching chips fires a call. Each view shows a
 * per-entity (CDN platform, or country for Geo) line/area chart + ranked table;
 * Hit Rate is a rate (`%`, line-only, no Summary/Divide, no total). Subdomain-
 * scoped via the shared scope bar; 7/14/30-day or custom range. A zone with no
 * proxied subdomain renders an empty state.
 */
import { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SegmentedControl,
  Tabs,
  TabsList,
  TabsTrigger,
  Skeleton,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  useWafMultiCdnDiagram,
  useWafMultiCdnUnsupported,
  useWafPlatforms,
  type WafMultiCdnKind,
  type WafMultiCdnMetric,
} from '@/api/waf';
import { ChartCard, KpiTiles, RefreshButton, Stack } from '../../_shared/AnalyticsKit';
import { formatBytesSI, formatBytesAxisSI, formatCount, formatNum, formatPct } from '../../_shared/analytics-format';
import { countryName, getGeoCountry } from '../_shared/geo';
import { WafScopeBar, type WafScope } from './WafScopeBar';
import { WafRangeControl, type ResolvedRange } from './WafRangeControl';
import { intervalForRange, multiCdnRows, multiCdnRanking, type ChartMode } from './diagram-transform';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

type MetricTab = 'traffic' | 'request';
type RankRow = { label: string; total: number };

/** Collapsed table size — Geo (many countries) shows this many, then "Show all". */
const ROW_LIMIT = 10;

const CHIP_ITEMS: { value: WafMultiCdnKind; label: string }[] = [
  { value: 'total', label: 'Total Bytes' },
  { value: 'origin', label: 'Origin Bytes' },
  { value: 'cacheRate', label: 'Hit Rate' },
  { value: 'geo', label: 'Geo Data' },
];
const CHIP_LABEL: Record<WafMultiCdnKind, string> = {
  total: 'Total Bytes',
  origin: 'Origin Bytes',
  cacheRate: 'Hit Rate',
  geo: 'Geo Data',
};

export function WafMultiCdnAnalytics({ serviceId }: { serviceId: string }) {
  const tabKey = `waf:${serviceId}:multi-cdn:tab`;
  const chipKey = `waf:${serviceId}:multi-cdn:chip`;
  const modeKey = `waf:${serviceId}:multi-cdn:mode`;

  const [scope, setScope] = useState<WafScope>({});
  const [range, setRange] = useState<ResolvedRange | null>(null);
  const [tab, setTab] = useState<MetricTab>(() => loadJSON<MetricTab>(tabKey, 'traffic'));
  const [chip, setChip] = useState<WafMultiCdnKind>(() => loadJSON<WafMultiCdnKind>(chipKey, 'total'));
  const [mode, setMode] = useState<ChartMode>(() => loadJSON<ChartMode>(modeKey, 'summary'));
  const [showAllRows, setShowAllRows] = useState(false);
  const [unsupportedOpen, setUnsupportedOpen] = useState(false);

  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to chart.
  const noSubdomain = !!scope.zone && !domain;
  const interval = range ? intervalForRange(range.start_time, range.end_time) : 86400;
  const hasScope = !!domain && !!range;

  const isHitRate = chip === 'cacheRate';
  const isTotal = chip === 'total';
  const isTraffic = tab === 'traffic';
  const isGeo = chip === 'geo';
  // The tab selects the series; for Hit Rate it picks traffic- vs request-based %.
  const metricKey: WafMultiCdnMetric = isTraffic ? 'traffic' : 'request';

  const platformsQ = useWafPlatforms(serviceId);
  const labelFor = useMemo(() => {
    // ISO country code → display name; unknown codes show "undefined" (not the raw code).
    if (isGeo) return (code: string) => (getGeoCountry(code) ? countryName(code) : 'undefined');
    const map = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name || p.name]));
    // Platform id 0 = served from origin (no CDN platform).
    return (id: string) => (id === '0' ? 'Origin' : map.get(id) ?? id);
  }, [isGeo, platformsQ.data]);
  // CDN platforms that don't report this metric (edge/total always supports all).
  const unsupportedQ = useWafMultiCdnUnsupported(serviceId, chip, !isTotal);
  // Resolve ids → display names; drop any id we can't map to a known platform; sort A→Z.
  const unsupportedNames = useMemo(() => {
    const map = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name || p.name]));
    return (unsupportedQ.data ?? [])
      .map((id) => map.get(id))
      .filter((n): n is string => !!n)
      .sort((a, b) => a.localeCompare(b));
  }, [unsupportedQ.data, platformsQ.data]);

  // One query per chip — switching Traffic/Request reuses the cached result.
  const q = useWafMultiCdnDiagram(
    serviceId,
    chip,
    {
      domain: domain ?? '',
      start_time: range?.start_time ?? 0,
      end_time: range?.end_time ?? 0,
    },
    hasScope,
  );

  const { rows, series } = useMemo(
    () => multiCdnRows(q.data, interval, labelFor, metricKey),
    [q.data, interval, labelFor, metricKey],
  );
  const ranking = useMemo(() => multiCdnRanking(q.data, metricKey), [q.data, metricKey]);
  const total = useMemo(() => ranking.reduce((a, r) => a + r.total, 0), [ranking]);
  const canCollapse = ranking.length > ROW_LIMIT;
  const visibleRanking = showAllRows ? ranking : ranking.slice(0, ROW_LIMIT);

  // Show real skeletons while the query resolves — never a misleading "0".
  const loading = !hasScope || q.isPending || q.isFetching;
  // Summary/Divide and the area fill only apply to non-rate metrics.
  const areaMode = mode === 'divide' && !isHitRate;

  const changeTab = (t: MetricTab) => {
    setTab(t);
    saveJSON(tabKey, t);
  };
  const changeChip = (c: WafMultiCdnKind) => {
    setChip(c);
    saveJSON(chipKey, c);
    setShowAllRows(false); // collapse the table back to the top rows
  };
  const changeMode = (m: ChartMode) => {
    setMode(m);
    saveJSON(modeKey, m);
  };

  const valueLabel = isHitRate ? 'Hit rate' : isTraffic ? 'Traffic' : 'Requests';
  const tableCols = useMemo<ColumnDef<RankRow, unknown>[]>(
    () => [
      {
        id: 'label',
        accessorKey: 'label',
        header: isGeo ? 'Country' : 'CDN platform',
        cell: ({ row }) => labelFor(row.original.label) || '—',
      },
      {
        id: 'total',
        accessorKey: 'total',
        header: valueLabel,
        cell: ({ row }) =>
          isHitRate
            ? formatPct(row.original.total, '0..100')
            : isTraffic
              ? formatBytesSI(row.original.total)
              : formatNum(row.original.total),
      },
    ],
    [isGeo, isHitRate, isTraffic, valueLabel, labelFor],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Layers size={22} />}
        title="Multi-CDN"
        description="CDN platform breakdown for the selected subdomain."
      />

      <div className={styles.toolbar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
        <div className={styles.controls}>
          <WafRangeControl serviceId={serviceId} pageKey="multi-cdn" onChange={setRange} />
        </div>
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Layers size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to monitor. Proxy a record to see Multi-CDN analytics."
        />
      ) : (
        <Stack>
          <Tabs value={tab} onValueChange={(v) => changeTab(v as MetricTab)}>
            <TabsList>
              <TabsTrigger value="traffic">Traffic</TabsTrigger>
              <TabsTrigger value="request">Request</TabsTrigger>
            </TabsList>
          </Tabs>

          <SegmentedControl
            aria-label="Data source"
            size="md"
            items={CHIP_ITEMS}
            value={chip}
            onValueChange={(v) => changeChip(v as WafMultiCdnKind)}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--vn-space-4)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 160 }}>
              {!isHitRate && (
                <KpiTiles
                  columns={1}
                  loading={loading}
                  items={[
                    isTraffic
                      ? { label: 'Total traffic', value: formatBytesSI(total) }
                      : { label: 'Total requests', value: formatCount(total) },
                  ]}
                />
              )}
            </div>
            <div className={styles.controlsRight}>
              {!isTotal && (
                <Button variant="secondary" size="md" onClick={() => setUnsupportedOpen(true)}>
                  Unsupported CDNs{unsupportedNames.length ? ` (${unsupportedNames.length})` : ''}
                </Button>
              )}
              {!isHitRate && (
                <Select value={mode} onValueChange={(v) => changeMode(v as ChartMode)}>
                  <SelectTrigger aria-label="Chart mode" className={styles.modeSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="divide">Divide</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <RefreshButton onRefresh={() => q.refetch()} />
            </div>
          </div>

          <div className={areaMode ? styles.denseFill : undefined}>
            <ChartCard
              title={CHIP_LABEL[chip]}
              description={
                isHitRate
                  ? 'Cache hit rate per CDN platform over the selected range.'
                  : `${isTraffic ? 'Traffic' : 'Request'} volume per ${isGeo ? 'country' : 'CDN platform'} over the selected range.`
              }
              data={rows}
              xKey="t"
              series={series}
              type={areaMode ? 'area' : 'line'}
              height={280}
              stacked={areaMode}
              showLegend
              // `key` is set for the tooltip only → full unit there, compact Y-axis.
              valueFormatter={
                isHitRate
                  ? (v) => formatPct(Number(v), '0..100')
                  : isTraffic
                    ? (v, key) => (key ? formatBytesSI(Number(v)) : formatBytesAxisSI(Number(v)))
                    : (v) => formatCount(Number(v))
              }
              loading={loading}
            />
          </div>

          {loading ? (
            <Skeleton style={{ height: 240 }} />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <DataTable
                columns={tableCols}
                data={visibleRanking}
                getRowId={(r) => r.label}
                emptyState="No Multi-CDN activity in the selected range."
              />
              {canCollapse && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button variant="secondary" size="sm" onClick={() => setShowAllRows((v) => !v)}>
                    {showAllRows ? 'Show less' : `Show all (${ranking.length})`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Stack>
      )}

      <Dialog open={unsupportedOpen} onOpenChange={setUnsupportedOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Unsupported CDN platforms</DialogTitle>
            <DialogDescription>
              These CDN platforms don&apos;t report {CHIP_LABEL[chip]} data, so they&apos;re absent from
              this chart.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {unsupportedQ.isPending ? (
              <Skeleton style={{ height: 64 }} />
            ) : unsupportedNames.length === 0 ? (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                All CDN platforms support this report.
              </span>
            ) : (
              <div
                style={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: '1px solid var(--vn-border-default)',
                  borderRadius: 'var(--vn-radius-md)',
                }}
              >
                {unsupportedNames.map((name, i) => (
                  <div
                    key={name}
                    style={{
                      padding: 'var(--vn-space-2) var(--vn-space-3)',
                      fontSize: 'var(--vn-font-size-sm)',
                      borderTop: i === 0 ? undefined : '1px solid var(--vn-border-default)',
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setUnsupportedOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default WafMultiCdnAnalytics;
